/**
* @summary - Component that Provides Asset Category Listing Page
* @author Aro J
* @since 2024-07-15
*/
import { Box, Stack } from '@mui/material';
import axios from "axios";
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DefaultCard } from '../../assets/styled-components/card';
import { MasterGraph } from '../../utility/apiURLS';
import { getAuthToken, getFacilityId } from "../../utility/tokenUtility";
import CloseButton from '../commoncomponents/CloseButton';
import CreateNewBtn from "../commoncomponents/CreateNewBtn";
import CustomAgGrid from '../commoncomponents/CustomAgGrid';
import CustomDialog from '../commoncomponents/CustomDialog';
import PageTitle from "../commoncomponents/PageTitle";
import RefreshExportButtons from '../commoncomponents/RefreshExportButtons';
import SectionTitle from '../commoncomponents/SectionTitle';
import { set } from 'react-hook-form';
import { is } from '@react-spring/shared';
import { useAccess } from "../../AccessContext";
import utilities from '../../utility/utilities';

const columns = [
  { field: 'warehousecode', headerName: 'Store Code' },
  { field: 'warehousedesc', headerName: 'Description' },
  { field: 'personincharge', headerName: 'Person-Incharge' },
  { field: 'warehouseaddress1', headerName: 'Address 1' },
  { field: 'warehousecity', headerName: 'City' },
  { field: 'warehousephoneno', headerName: 'Telephone' },
  { field: 'warehouseemail', headerName: 'Email ' },

];
const ListStoreDetails = () => {
  const [rows, setRows] = useState([]);
  const [filteredRows, setFilteredRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [deletedRowDetails, setDeletedRowDetails] = useState({});
  const [errorMessage, setErrorMessage] = useState("");
  const navigate = useNavigate();
  const [gridApi, setGridApi] = useState(null);
  const[isLoading, setIsLoading]=useState(false);
  const [filterModel, setFilterModel] = useState([]);
  const [sortModel, setSortModel] = useState([]);
  const [pageIndex, setPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [rowCount, setRowCount] = useState(0);
  const [exportData, setExportData] = useState([]);
  const [exportCount, setExportCount] = useState(0)

  const accessData = useAccess();
  const access = utilities.getScreenPermissions(accessData, "IMSM001");

  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const facilityid = getFacilityId('facilityid');

  const fetchAssetCategories = async () => {
    setIsLoading(true);
    try { 
      const response = await axios.get(`${MasterGraph}/api/graph/GetStoredetailsbyFacilityId/${facilityid}?page=${pageIndex}&size=${pageSize}&filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`, config);

      setRows(response.data.items);
      setFilteredRows(response.data.items);
      setRowCount(response.data.totalCount);
      setExportCount(response.data.totalCount)

    } catch (error) {
      console.error("Error fetching Store Details:", error);
    }
    finally{
      setIsLoading(false);
    }

  };



  const fetchExportData = async () => {
   
    try { 
      const response = await axios.get(`${MasterGraph}/api/graph/GetExportData/${facilityid}?filterOptions=${JSON.stringify(
          filterModel
        )}&sortOptions=${JSON.stringify(sortModel)}`, config);

        setExportData(response.data)


    } catch (error) {
      console.error("Error fetching Store Details:", error);
    }
    
  };


  useEffect(() => {
    fetchAssetCategories();

    
  }, [filterModel, sortModel, pageIndex, pageSize]);

  const handleDelete = (row) => {
    setRowToDelete(row);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (rowToDelete) {
      const token = getAuthToken("id_token");
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const isDeleted = await deleteAssetCategory(rowToDelete.id, config);
      setDeleteDialogOpen(false);
      if (isDeleted) {
        setDeletedRowDetails({ code: rowToDelete.assetcategory, desc: rowToDelete.assetcategorydesc });
        setConfirmationDialogOpen(true);
      }
      setRowToDelete(null);
    }
  };

  const deleteAssetCategory = async (id, config) => {
    try {
      await axios.delete(`${MasterGraph}/api/masimswarehousedetails/${id}`, config);
      fetchAssetCategories();
      return true;
    } catch (error) {
      console.error("Error deleting Store Code:", error);
      if (error.response && error.response.status === 400) {
        setErrorMessage("Record cannot be deleted as it is referred in another record.");
      } else {
        setErrorMessage("An error occurred while deleting the Store Details. Please try again.");
      }
      return false;
    }
  };



  const handleCreateClick = () => {
    navigate("/storedetails/register");
  };

  const handleEdit = (rowData) => {
    navigate("register", { state: { ...rowData, readOnlyEdit: true, mode: 'edit' } });
  };

  const handleView = (rowData) => {
    navigate("register", { state: { ...rowData, readOnly: true, mode: 'view' } });
  };

  const handleRefresh = () => {
    setPageIndex(1);
    setPageSize(10);
    setFilterModel([])
    setSortModel([])
  };


  return (
    <>
      <PageTitle title={"Store Details"} />
      <DefaultCard>
        <Stack
          mb={1}
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <SectionTitle title={"Store Details List"} />

          <Stack direction={"row"} spacing={1.5}>
           {access?.canCreate && <CreateNewBtn
              onClick={handleCreateClick}
            />}
            <RefreshExportButtons
              exportFunction={fetchExportData}
              exportData={exportData}
              refData={gridApi}
              handleRefresh={handleRefresh}
              title={"Store Details"}
            />
          </Stack>
        </Stack>
        <Box>
          <CustomAgGrid
            rows={filteredRows}
            columns={columns}
            handleEdit={access?.canEdit && handleEdit}
            handleDelete={access?.canDelete && handleDelete}
            handleView={access?.canView && handleView}
            setGridApi={setGridApi}
            isLoading={isLoading}
            pagination={false}
            setFilterModel={setFilterModel}
            setSortModel={setSortModel}
            totalCount={rowCount}
            pageIndex={pageIndex}
            pageSize={pageSize}
            setPageIndex={setPageIndex}
            setPageSize={setPageSize}
          />
        </Box>

        <CustomDialog
          open={deleteDialogOpen}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={confirmDelete}

          content="Are you sure you want to delete?"
          cancelBtn='No'
          confirmBtn="Yes"
        />
        <CustomDialog
          open={confirmationDialogOpen}
          onClose={() => setConfirmationDialogOpen(false)}

          content="Record deleted successfully."
          confirmBtn="OK"
          onConfirm={() => setConfirmationDialogOpen(false)}
        />
        <CustomDialog
          open={!!errorMessage}
          onClose={() => setErrorMessage("")}
          onConfirm={() => setErrorMessage("")}
          content={errorMessage}
          confirmBtn="Ok"
        />
      </DefaultCard>
      <CloseButton />
    </>
  );
};

export default ListStoreDetails;
